import { SentryTeamNames } from '@frontend/sentry-teams';
import { otel } from './otel/otel';

export type { SentryTeamNames };

export interface MetricsLoggerConfig {
  transports?: {
    console?: { enabled?: boolean };
    sentry?: { enabled?: boolean };
    otel?: {
      enabled?: boolean;
      endpoint?: string;
      serviceName?: string;
      headers?: Record<string, string>;
      getAuthToken?: () => string | undefined | Promise<string | undefined>;
      timeoutMs?: number;
    };
  };
}

export interface MetricAttributes {
  team: SentryTeamNames;
  category: string;
  [key: string]: string | number | boolean | undefined;
}

export interface MetricOptions {
  attributes: MetricAttributes;
  transports?: { console?: boolean; sentry?: boolean; otel?: boolean };
}

class WeaveMetricsLogger {
  private config: MetricsLoggerConfig = {
    transports: {
      console: { enabled: import.meta.env.DEV },
      sentry: { enabled: false },
      otel: { enabled: true },
    },
  };

  public initialize(config: MetricsLoggerConfig) {
    this.config = {
      transports: {
        console: { enabled: config.transports?.console?.enabled ?? this.config.transports?.console?.enabled ?? true },
        sentry: { enabled: config.transports?.sentry?.enabled ?? this.config.transports?.sentry?.enabled ?? true },
        otel: {
          enabled: config.transports?.otel?.enabled ?? this.config.transports?.otel?.enabled ?? false,
          endpoint: config.transports?.otel?.endpoint ?? this.config.transports?.otel?.endpoint,
          serviceName:
            config.transports?.otel?.serviceName ?? this.config.transports?.otel?.serviceName ?? 'weave-frontend',
          headers: config.transports?.otel?.headers ?? this.config.transports?.otel?.headers,
          getAuthToken: config.transports?.otel?.getAuthToken ?? this.config.transports?.otel?.getAuthToken,
          timeoutMs: config.transports?.otel?.timeoutMs ?? this.config.transports?.otel?.timeoutMs,
        },
      },
    };

    if (this.isTransportEnabled('otel') && this.config.transports?.otel?.endpoint) {
      otel.init({
        endpoint: this.config.transports.otel.endpoint,
        serviceName: this.config.transports.otel.serviceName,
        headers: this.config.transports.otel.headers,
        getAuthToken: this.config.transports.otel.getAuthToken,
        timeoutMillis: this.config.transports.otel.timeoutMs,
      });
    }
  }

  private isTransportEnabled(name: 'console' | 'sentry' | 'otel', override?: boolean): boolean {
    return override !== undefined ? override : !!this.config.transports?.[name]?.enabled;
  }

  public metric(name: string, value: number, options: MetricOptions) {
    const { attributes, transports } = options;

    if (this.isTransportEnabled('console', transports?.console)) {
      console.debug(`[Metric] ${name} = ${value}`, attributes);
    }

    if (this.isTransportEnabled('sentry', transports?.sentry)) {
      //TODO - add Sentry Application Metrics Here
    }

    if (this.isTransportEnabled('otel', transports?.otel)) {
      otel.metric(name, value, attributes as Record<string, string | number | boolean>);
    }
  }
}

export const metricsLogger = new WeaveMetricsLogger();
